<script lang="ts">
import { defineComponent } from "vue";
import _ from "@/boot/lodash";
import RequestPromises from "@/mixins/requestPromises";
import ProvisioningProviderInjectorMixin from "@/mixins/provisioningProviderInjectorMixin";
import { QUERY_PARAMS } from "@/data/constants";
import { DeferModes } from "@/data/enums/provisioning";
import type { PropType, CreateElement } from "vue";
import type { IProductProvisionProviderData } from "@/models/providers/provisioning";
import type { IProviderMethods } from "@/models/providers";
import type { IBasket, IBasketProduct } from "@/models/baskets";
import type { IProduct } from "@/models/products";
import type {
  IBasketProvisioningFieldsForm,
  IBlueprintField
} from "@/models/provisioning";
import type { IClient } from "@/models/clients";

export default defineComponent({
  name: "ProductProvisionProvider",
  mixins: [RequestPromises, ProvisioningProviderInjectorMixin],
  provide() {
    return {
      productProvisionData: this
        .productProvisionData as IProductProvisionProviderData,
      productProvisionMethods: this.productProvisionMethods as IProviderMethods
    };
  },
  props: {
    basketId: { type: String as PropType<IBasket["id"]>, required: true },
    basketProduct: { type: Object as PropType<IBasketProduct> },
    product: { type: Object as PropType<IProduct>, required: true },
    subProductIds: {
      type: Array as PropType<IProduct["id"][]>,
      required: true
    },
    clientId: { type: String as PropType<IClient["id"]> },
    handleLoading: { type: Boolean, default: true },
    loaderAttrs: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({})
    },
    handleRequestError: { type: Boolean, default: true },
    initialProductForm: { type: Boolean, required: true }
  },
  data: () => ({
    isLoading: false,
    isReloading: false,
    isUpdating: false,
    hasRequestError: false,
    savedFormValues: {},
    provisionFieldValues:
      {} as IBasketProvisioningFieldsForm["provision_field_values"],
    productFields: [] as IBlueprintField[]
  }),
  computed: {
    basketProductId() {
      return _.get(this.basketProduct, "id", null);
    },
    productId() {
      return _.get(this.product, "id", "");
    },
    productFieldsValues() {
      return this.basketProductId && !this.initialProductForm
        ? this.$provisionMethods.basketProductFieldsValues(
            this.basketId,
            this.basketProductId
          )
        : [];
    },
    hasProvisioning() {
      return (
        (this.basketProductId &&
          !!_.get(this.basketProduct, "product.provision_blueprint_id")) ||
        !!_.get(this.product, "provision_blueprint_id")
      );
    },
    pathFieldValues() {
      const pathFieldValues = {};

      _.forEach(_.get(this.$route, "query", {}), (queryValue, queryKey) => {
        if (queryKey.includes(QUERY_PARAMS.PRODUCT_FIELDS)) {
          pathFieldValues[
            `${queryKey
              .replace(`${QUERY_PARAMS.PRODUCT_FIELDS}[`, "")
              .replace("]", "")}`
          ] = queryValue;
        }
      });

      return pathFieldValues;
    },
    productProvisionMethods(): IProviderMethods {
      return {
        reloadData: this.reloadData
      };
    }
  },
  watch: {
    subProductIds: { handler: "refreshProvisionFields" }
  },
  created() {
    if (!this.hasProvisioning) return;
    this.isLoading = true;
    this.loadData();
  },
  beforeDestroy() {
    if (this.hasProvisioning) {
      if (this.basketProductId) {
        this.$provisionMethods.destroyBasketProductFields(
          this.basketId,
          this.basketProductId
        );
        this.$provisionMethods.destroyFieldsValues(
          this.basketId,
          this.basketProductId
        );
      }
      if (this.initialProductForm) {
        this.$provisionMethods.destroyProductFields(this.productId);
      }
    }
  },
  methods: {
    async loadData() {
      try {
        await this.loadProvisioning(
          _.isEmpty(this.basketProduct) && !this.subProductIds.length
            ? undefined
            : this.subProductIds
        );

        this.initiateProductFields();
        this.initialiseForm();

        this.hasRequestError = false;
      } catch (error) {
        this.$emit("handleError", error); // Handle error by a parent../0.

        this.$store.dispatch("api/handleError", error);

        this.hasRequestError = true;
      } finally {
        this.isLoading = false;
        this.isReloading = false;
      }
    },
    async reloadData() {
      this.isReloading = true;
      this.loadData();
    },
    async loadProvisioning(subProductIds: IProduct["id"][] | null = null) {
      if (!this.basketProductId) {
        return this.$provisionMethods.getProductFields(
          this.productId,
          this.clientId,
          subProductIds
        );
      }

      return Promise.all([
        this.$provisionMethods.getBasketProductFields(
          this.basketId,
          this.basketProductId,
          subProductIds,
          true
        ),
        this.$provisionMethods.getFieldsValues(
          this.basketId,
          this.basketProductId,
          subProductIds,
          true
        )
      ]);
    },
    initialiseForm() {
      if (!this.basketProductId) {
        this.$emit("initialiseForm");

        _.forEach(this.productFields, (field: any) => {
          this.$set(
            this.provisionFieldValues,
            field.name,
            _.get(
              this.pathFieldValues,
              field.name,
              _.get(field, "default_value", "")
            )
          );
        });
        this.$emit("updateForm", this.provisionFieldValues);
        return;
      }

      const previousProvisionValues =
        this.$provisionData.savedBasketProductValues[
          `${this.basketId}_${this.basketProductId}`
        ] || {};

      _.forEach(this.productFields, (field: any) => {
        if (!_.isEmpty(previousProvisionValues)) {
          this.$set(
            this.provisionFieldValues,
            `${field.name}`,
            _.has(previousProvisionValues, field.name)
              ? previousProvisionValues[field.name]
              : ""
          );
        } else {
          this.$set(
            this.provisionFieldValues,
            `${field.name}`,
            _.get(
              this.productFieldsValues,
              field.name,
              _.isEmpty(this.productFieldsValues)
                ? _.isNull(field.default_value)
                  ? ""
                  : field.default_value
                : ""
            )
          );
        }
      });

      this.$emit("updateForm", this.provisionFieldValues);
      this.$emit("initialiseForm");
    },
    updateProvisioningForm() {
      // Save and remove values from previous configuration
      _.forEach(_.keys(this.provisionFieldValues), (fieldKey: string) => {
        if (_.some(this.productFields, ["name", fieldKey])) return;

        this.$set(
          this.savedFormValues,
          fieldKey,
          this.provisionFieldValues[fieldKey]
        );

        delete this.provisionFieldValues[fieldKey];
      });

      _.forEach(this.productFields, (field: any) => {
        // If value was already touched, leave it
        if (_.has(this.provisionFieldValues, field.name)) return;

        // if value was removed from a previous configuration, bring it back
        if (_.has(this.savedFormValues, field.name)) {
          this.$set(
            this.provisionFieldValues,
            field.name,
            _.get(this.savedFormValues, field.name)
          );
          return;
        }

        // If not basket product and field was not touched before, use field value from the path or default value
        if (!this.basketProductId) {
          this.$set(
            this.provisionFieldValues,
            field.name,
            _.get(
              this.pathFieldValues,
              field.name,
              _.get(field, "default_value", "")
            )
          );
          return;
        }

        // If basket product configuration is changed
        if (_.isEmpty(this.savedFormValues)) {
          if (_.has(this.productFieldsValues, field.name)) {
            this.$set(
              this.provisionFieldValues,
              `${field.name}`,
              _.get(this.productFieldsValues, field.name)
            );
          }
          return;
        }

        // If basket product configuration is not changed
        this.$set(
          this.provisionFieldValues,
          `${field.name}`,
          _.get(field, "default_value", "")
        );
      });

      this.$emit("updateForm", this.provisionFieldValues);
    },
    initiateProductFields() {
      this.productFields = _.filter(
        this.initialProductForm
          ? this.$provisionMethods.productFields(this.productId)
          : this.$provisionMethods.basketProductFields(
              this.basketId,
              this.basketProductId || ""
            ),
        (field: IBlueprintField) => field.defer_mode !== DeferModes.HIDDEN
      );
    },
    async refreshProvisionFields() {
      try {
        this.isUpdating = true;

        if (_.isEmpty(this.basketProduct)) {
          await this.loadProvisioning(
            _.isEmpty(this.subProductIds) ? undefined : this.subProductIds
          );
        } else {
          await this.loadProvisioning(this.subProductIds);
        }

        this.initiateProductFields();
        this.updateProvisioningForm();

        this.hasRequestError = false;
      } catch (error) {
        this.$emit("handleError", error); // Handle error by a parent
        this.$store.dispatch("api/handleError", error);

        this.hasRequestError = true;
      } finally {
        this.isUpdating = false;
        this.isReloading = false;
      }
    },
    productProvisionData(): IProductProvisionProviderData {
      return {
        productFields: this.productFields,
        isUpdating: this.isUpdating,
        isLoading: this.isLoading,
        isReloading: this.isReloading,
        hasRequestError: this.hasRequestError
      };
    }
  },
  render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", {
        props: { if: true },
        attrs: this.loaderAttrs
      });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $productProvisionData: this.productProvisionData(),
        $productProvisionMethods: this.productProvisionMethods
      })
    );
  }
});
</script>
